import { AlertTriangle, Loader2, RefreshCw, Shield, UserRound } from "lucide-react";
import { useState } from "react";
import { FormModal, useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { useUpdateDataSource } from "@/http/mutations/data-source/useUpdateDataSource";
import { getApiErrorMessage } from "@/lib/api";
import { dataSourceService } from "@/lib/services";
import type { DataCollection, SyncSchedule } from "@/types";
import { SyncScheduleSection } from "./SyncScheduleSection";

const REAUTH_MESSAGE = "Automated sync requires re-authorization.";

function needsReauthorization(collection: DataCollection): boolean {
  return collection.status === "error" && collection.errorMessage === REAUTH_MESSAGE;
}

export function AutomatedSyncConsentText() {
  return (
    <div className="space-y-3 text-sm text-content-secondary">
      <p>
        Anthm will keep this SharePoint folder synced on your behalf, using your Microsoft permissions. Anthm will not
        gain access to anything you cannot already access.
      </p>
      <p>
        This stays on until you turn it off. If it goes unused for 60 days, you'll be asked to re-authorize. You can
        revoke it at any time from your account settings.
      </p>
    </div>
  );
}

export function AutomatedSyncControl({
  collection,
  onUpdated,
}: Readonly<{ collection: DataCollection; onUpdated: () => Promise<void> | void }>) {
  const [showConsent, setShowConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  const { profile } = useAuth();
  const updateDataSource = useUpdateDataSource();

  // Backend-gated: hide the whole control when delegated token caching is
  // disabled server-side. Otherwise users could toggle it on, read the consent,
  // and only then hit a server-side failure. Single source of truth is the
  // backend capability flag from GET /auth/me.
  if (!profile?.capabilities?.automatedSyncEnabled) return null;

  if (collection.sourceType !== "sharepoint") return null;

  const enabled = Boolean(collection.automatedSyncEnabled);
  const reauth = needsReauthorization(collection);
  const ownerLabel = collection.syncOwnerName || collection.syncOwnerEmail || null;

  const applyChange = async (enable: boolean) => {
    try {
      setBusy(true);
      await dataSourceService.update(collection.uuid, { enableAutomatedSync: enable });
      showToast(enable ? "Automated sync enabled" : "Automated sync disabled", "success");
      setShowConsent(false);
      await onUpdated();
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to update automated sync", "error");
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = (syncSchedule: SyncSchedule | null) =>
    updateDataSource.mutateAsync(
      { collectionUuid: collection.uuid, data: { syncSchedule } },
      {
        onSuccess: () => {
          showToast(syncSchedule ? "Sync schedule saved" : "Sync schedule removed", "success");
          void onUpdated();
        },
        onError: (err) => showToast(getApiErrorMessage(err) || "Failed to update sync schedule", "error"),
      },
    );

  const handleToggle = () => {
    if (enabled) {
      void applyChange(false);
    } else {
      setShowConsent(true);
    }
  };

  return (
    <div className="mb-3 px-3 py-2 bg-surface-secondary/50 rounded-lg">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-2 min-w-0">
          <Shield className="h-4 w-4 text-content-tertiary mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-content-primary">Automated sync</p>
            <p className="text-xs text-content-tertiary">
              Keep this folder synced in the background using your Microsoft permissions.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {reauth && (
            <button
              type="button"
              onClick={() => setShowConsent(true)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-400 hover:bg-interactive-hover rounded-lg disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />
              Re-authorize
            </button>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label="Toggle automated sync"
            onClick={handleToggle}
            disabled={busy}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
              enabled ? "bg-brand-primary" : "bg-surface-tertiary"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {reauth && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          Your saved permission is no longer valid. Re-authorize to resume background sync.
        </p>
      )}

      {enabled && !reauth && (
        <div className="mt-3 space-y-3">
          {ownerLabel && (
            <p className="flex items-center gap-1.5 text-xs text-content-tertiary">
              <UserRound className="h-3.5 w-3.5 shrink-0" />
              Synced as {ownerLabel}
            </p>
          )}
          <SyncScheduleSection
            schedule={collection.config?.sync_schedule}
            onSave={async (schedule) => {
              await saveSchedule(schedule);
            }}
            onRemove={async () => {
              await saveSchedule(null);
            }}
            disabled={busy || updateDataSource.isPending}
          />
        </div>
      )}

      {showConsent && (
        <FormModal
          title="Enable automated sync"
          loading={busy}
          maxWidth="max-w-lg"
          onClose={() => !busy && setShowConsent(false)}
          onSubmit={(e) => {
            e.preventDefault();
            void applyChange(true);
          }}
          footer={
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConsent(false)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-content-secondary hover:bg-interactive-hover rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary/90 rounded-lg disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Authorize
              </button>
            </div>
          }
        >
          <AutomatedSyncConsentText />
        </FormModal>
      )}
    </div>
  );
}
